import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { parcellesService } from "../../services";
import { CustomDialog, Loading, showToast } from "../../components";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../../utils/UserProvider";
import { AddressAutocomplete } from "../AddressAutocomplete";
import { consoleLoggingIntegration } from "@sentry/core";
 
interface ModalAddParcelleProps {
  closeModalParcelle: () => void;
  modalIsOpenParcelle: boolean;
}
 
const ModalAddParcelle = ({
  closeModalParcelle,
  modalIsOpenParcelle,
}: ModalAddParcelleProps) => {
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState<boolean>(false);
  const { client } = useAuth();
  
 
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm();
 
  const onSubmit = async (data) => {
    try {
      const parcelleData = {
        numéro: parseInt(data.numéro, 10),
        env_client_id: client?.id,
        numéro_section: data.numéro_section,
        id_commune: data.id_commune,
        nom_commune:data.region_commune,
      };
 
      setLoading(true);
      await parcellesService.post(parcelleData);
      setLoading(false);
      showToast("success", "Parcelle ajoutée avec succès");
      queryClient.refetchQueries({ queryKey: ["parcelles"] });
      closeModalParcelle();
      reset();

    } catch (e) {
      setLoading(false);
      showToast("error", e.message || "Erreur lors de l'ajout de la parcelle");
    }
  };
 
  const handleAdresseSelect = async (val, result) => {

    if (!result || !result.lat || !result.lon) {
      console.warn("Adresse invalide ou sans coordonnées");
      setValue("numéro_section", "");
      setValue("numéro", "");
      setValue("region_commune","");
      setValue("id_commune","");
      return;
    }
 
    const lat = parseFloat(result.lat);
    const lon = parseFloat(result.lon);
   
    const geom = { type: "Point", coordinates: [lon, lat] };

    try {
      setLoading(true);
      const url = `https://apicarto.ign.fr/api/cadastre/parcelle?geom=${encodeURIComponent(
        JSON.stringify(geom)
      )}&_limit=1`;
    
      const response = await fetch(url);

      if (!response.ok) {
        showToast("error", "Aucune parcelle trouvée pour cette adresse");
        setValue("numéro_section", "");
        setValue("numéro", "");
        setValue("region_commune","");
        setValue("id_commune","");
        setLoading(false);
        return;
      }
      const data = await response.json();
      if (data.features?.length > 0) {
        const parcelle = data.features[0].properties;
        setValue("numéro_section", parcelle.section || "");
        setValue("numéro", parcelle.numero || "");
        setValue("region_commune",parcelle.nom_com);
        setValue("id_commune",parcelle.code_insee);
      

      } else {
        showToast("error", "Aucune parcelle trouvée pour cette adresse");
        setValue("numéro_section", "");
        setValue("numéro", "");
        setValue("region_commune","");
        setValue("id_commune","");
      }
      setLoading(false);
    } catch (err) {
      console.error(err);
      showToast("error", "Erreur lors de la récupération des informations cadastrales");
      setValue("numéro_section", "");
      setValue("numéro", "");
      setValue("region_commune","");
      setValue("id_commune","");
      setLoading(false);
    }
  };


 
 
  return (
    <>
      <CustomDialog
        header="Ajouter une parcelle"
        visible={modalIsOpenParcelle}
        closeModalDialog={() => {
          closeModalParcelle();
          reset();
        }}
      >
        <div className="w-full h-full relative">
          <div className="p-6 space-y-6">

            <form onSubmit={handleSubmit(onSubmit)} encType="multipart/form-data">
              <div className="grid grid-cols-6 gap-6">
                {/* Adresse */}
                
                <div className="col-span-6">
                  <label className="block text-sm font-bold text-gray-900">Recherche par adresse *</label>
                  <AddressAutocomplete value="" onChange={handleAdresseSelect} />
                </div>
            
 
                {/* Numéro de section */}
                <div className="col-span-6 sm:col-span-6">
                  <label
                    htmlFor="numéro_section"
                    className="block text-sm font-bold leading-6 text-gray-900"
                  >
                    Numéro de section *
                  </label>
                  <input
                    type="text"
                    id="numéro_section"
                    placeholder="Numéro section"
                    className="my-5 border border-gray-300 text-gray-900 text-md font-normal rounded-lg focus:ring-gexpertise-100 focus:border-gexpertise-200 block w-full p-2.5"
                    {...register("numéro_section", {
                      required: "Champ obligatoire",
                      pattern: {
                        value: /^[A-Z0-9]{1,4}$/,
                        message: "Format invalide (lettres et chiffres, max 4 caractères)",
                      },
                    })}
                  />
               
                </div>
 
                {/* Numéro de parcelle */}
                <div className="col-span-6 sm:col-span-6">
                  <label
                    htmlFor="numero"
                    className="block text-sm font-bold leading-6 text-gray-900"
                  >
                    Numéro de parcelle *
                  </label>
                  <input
                    type="text"
                    id="numéro"
                    placeholder="Numéro parcelle"
                    className="my-5 border border-gray-300 text-gray-900 text-md font-normal rounded-lg focus:ring-gexpertise-100 focus:border-gexpertise-200 block w-full p-2.5"
                    {...register("numéro", {
                      required: "Champ obligatoire",
                      pattern: {
                        value: /^[0-9]+$/,
                        message: "Le numéro de parcelle doit contenir uniquement des chiffres.",
                      },
                    })}
                  />
                
                </div>

                <div className="col-span-6">
                  <label className="block text-sm font-bold text-gray-900">ID Commune (Code INSEE) *</label>
                  <input
                    id="id_commune"
                    type="text"
                    className="mt-2 border border-gray-300 text-gray-900 rounded-lg block w-full p-2.5"
                    placeholder="Ex: 75001"
                    {...register("id_commune", { required: "Champ obligatoire" })}
                  />
                </div>


                <div className="col-span-6 sm:col-span-6">
                  <label
                    htmlFor="region_commune"
                    className="block text-sm font-bold leading-6 text-gray-900"
                  >
                    Commune de parcelle *
                  </label>
                  <input
                    type="text"
                    id="region_commune"
                    placeholder="Commune parcelle"
                    className="my-5 border border-gray-300 text-gray-900 text-md font-normal rounded-lg focus:ring-gexpertise-100 focus:border-gexpertise-200 block w-full p-2.5"
                    {...register("region_commune", {
                      required: "Champ obligatoire",
                      
                    })}
                  />
                
                </div>

                









              </div>
 
              <p className="text-lg font-thin my-4">* obligatoire</p>
              <div className="flex justify-end">
                <button
                  type="submit"
                  className="text-white mt-5 bg-gexpertise-200 hover:bg-gexpertise-100 font-medium rounded-lg text-md px-6 py-3 text-center"
                  disabled={loading}
                >
                  {loading && <span className="animate-spin mr-2">⏳</span>}
                  Ajouter
                </button>
              </div>
            </form>
          </div>
        </div>
      </CustomDialog>
      {loading && <Loading />}
    </>
  );
};
 
export default ModalAddParcelle;
 